/* eslint-disable max-lines -- arc hook logic is cohesive and already split utils */
import { useEffect, useRef } from 'react';
import {
  addMediaQueryListener,
  CENTER_ANGLE,
  CENTER_STEPS,
  clamp,
  CONFIG,
  createMediaQuery,
  DEG_TO_RAD,
  ILayout,
  IIntro,
  ROTATION_LIMIT_DESKTOP,
  ROTATION_LIMIT_MOBILE,
  smoothstep,
} from './realtorsArcUtils';

export function useRealtorsArc(arcRef: React.RefObject<HTMLElement | null>, cardSrcs: string): void {
  const stateRef = useRef({
    rotation: 0,
    velocity: 0,
    lastX: null as number | null,
    isTouching: false,
    lastScrollY: typeof window !== 'undefined' ? window.scrollY : 0,
  });
  const layoutRef = useRef<ILayout>({
    radius: 1215,
    centerOffset: 80,
    cardSize: 160,
    stepDeg: CONFIG.stepDegFallback,
    stepRad: CONFIG.stepDegFallback * DEG_TO_RAD,
    startAngle: 0,
    maxDelta: 0,
  });
  const introRef = useRef<IIntro>({
    active: false,
    start: 0,
    duration: 5200,
    from: 0,
    peak1: -6 * DEG_TO_RAD,
    peak2: 7 * DEG_TO_RAD,
    to: 0,
  });
  const configRef = useRef({ ...CONFIG });
  const cardsRef = useRef<Element[]>([]);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const arc = arcRef.current;
    if (!arc) {
      return;
    }

    const prefersReducedMotion = createMediaQuery('(prefers-reduced-motion: reduce)');
    const mobileQuery = createMediaQuery('(max-width: 768px)');
    const srcList = cardSrcs
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);

    const getCardCount = (): number => (mobileQuery.matches ? 11 : 9);
    const getRotationLimit = (): number => (mobileQuery.matches ? ROTATION_LIMIT_MOBILE : ROTATION_LIMIT_DESKTOP);

    const readLayout = (): void => {
      const styles = getComputedStyle(arc);
      let stepDeg = parseFloat(styles.getPropertyValue('--arc-step-deg')) || CONFIG.stepDegFallback;
      let stepRad = stepDeg * DEG_TO_RAD;

      const radius = parseFloat(styles.getPropertyValue('--arc-radius')) || 1215;
      const centerOffset = parseFloat(styles.getPropertyValue('--arc-center-offset')) || 80;
      const cardSize = parseFloat(styles.getPropertyValue('--card-size')) || 160;
      const cardCount = configRef.current.cardCount;

      if (mobileQuery.matches) {
        stepRad = (cardSize * 1.1) / radius;
        stepDeg = stepRad / DEG_TO_RAD;
      }

      layoutRef.current = {
        radius,
        centerOffset,
        cardSize,
        stepDeg,
        stepRad,
        startAngle: CENTER_ANGLE - ((cardCount - 1) * stepRad) / 2,
        maxDelta: ((cardCount - 1) * stepRad) / 2,
      };
    };

    const createCards = (): Element[] => {
      const count = configRef.current.cardCount;
      arc.innerHTML = '';
      const fragment = document.createDocumentFragment();
      for (let i = 0; i < count; i += 1) {
        const card = document.createElement('div');
        card.className = 'realtors-arc__card';
        const img = document.createElement('img');
        img.src = srcList[i % srcList.length] || '';
        img.alt = `Риелтор ${i + 1}`;
        card.append(img);
        fragment.append(card);
      }
      arc.append(fragment);

      return Array.from(arc.querySelectorAll('.realtors-arc__card'));
    };

    const updateCards = (cards: Element[]): void => {
      const width = arc.clientWidth;
      const height = arc.clientHeight;
      const halfWidth = width / 2;
      const layout = layoutRef.current;
      const state = stateRef.current;
      const fadeRange = layout.cardSize;
      const isMobile = mobileQuery.matches;

      cards.forEach((card, index) => {
        const angle = layout.startAngle + index * layout.stepRad + state.rotation;
        const x = Math.cos(angle) * layout.radius;
        const y = -Math.sin(angle) * layout.radius + layout.centerOffset;

        const delta = Math.abs(angle - CENTER_ANGLE);
        const steps = delta / layout.stepRad;
        const scale = Math.pow(CONFIG.scaleStep, steps);
        const t = clamp(delta / layout.maxDelta, 0, 1);
        const blur = isMobile ? 0 : CONFIG.maxBlur * t;

        const centerX = halfWidth + x;
        const centerY = layout.radius + y;
        const edgeDistance = Math.min(centerX, width - centerX, centerY, height - centerY);
        const edgeT = clamp(edgeDistance / fadeRange, 0, 1);
        const boundaryOpacity = smoothstep(edgeT);

        const mobileFadeRange = layout.cardSize * 0.35;
        const mobileOpacity =
          edgeDistance < mobileFadeRange ? smoothstep(clamp(edgeDistance / mobileFadeRange, 0, 1)) : 1;

        const opacity = isMobile
          ? mobileOpacity
          : delta <= layout.stepRad * CENTER_STEPS
            ? 1
            : (1 - t) * boundaryOpacity;

        const depth = 1 - t;

        (card as HTMLElement).style.transform = `translate(calc(-50% + ${x}px), calc(-50% + ${y}px)) scale(${scale})`;
        (card as HTMLElement).style.filter = `blur(${blur.toFixed(2)}px)`;
        (card as HTMLElement).style.opacity = opacity.toFixed(3);
        (card as HTMLElement).style.zIndex = `${Math.round(100 + depth * 100)}`;
      });
    };

    const onPointerMove = (clientX: number): void => {
      const state = stateRef.current;
      if (state.lastX === null) {
        state.lastX = clientX;

        return;
      }
      const deltaX = clientX - state.lastX;
      state.lastX = clientX;
      const speed = clamp(deltaX * CONFIG.speedFactor, -CONFIG.maxSpeed, CONFIG.maxSpeed);
      state.velocity += speed;
    };

    const onScroll = (): void => {
      const state = stateRef.current;
      const currentY = window.scrollY;
      const deltaY = currentY - state.lastScrollY;
      state.lastScrollY = currentY;
      const speed = clamp(deltaY * CONFIG.speedFactor, -CONFIG.maxSpeed, CONFIG.maxSpeed);
      state.velocity += speed;
    };

    const tick = (): void => {
      const state = stateRef.current;
      const intro = introRef.current;

      if (!state.isTouching) {
        state.velocity *= CONFIG.inertia;
      }
      state.velocity *= CONFIG.damping;

      if (intro.active && mobileQuery.matches) {
        const elapsed = performance.now() - intro.start;
        const t = clamp(elapsed / intro.duration, 0, 1);
        const easeInOutSine = (v: number) => 0.5 - 0.5 * Math.cos(Math.PI * v);

        const segmentA = Math.abs(intro.peak1 - intro.from);
        const segmentB = Math.abs(intro.peak2 - intro.peak1);
        const segmentC = Math.abs(intro.to - intro.peak2);
        const total = segmentA + segmentB + segmentC || 1;
        const tA = segmentA / total;
        const tB = tA + segmentB / total;

        if (t <= tA) {
          const localT = easeInOutSine(t / tA);
          state.rotation = intro.from + (intro.peak1 - intro.from) * localT;
        } else if (t <= tB) {
          const localT = easeInOutSine((t - tA) / (tB - tA));
          state.rotation = intro.peak1 + (intro.peak2 - intro.peak1) * localT;
        } else {
          const localT = easeInOutSine((t - tB) / (1 - tB));
          state.rotation = intro.peak2 + (intro.to - intro.peak2) * localT;
        }

        if (t >= 1) {
          intro.active = false;
          state.velocity = 0;
        }
      } else {
        const limit = getRotationLimit();
        state.rotation = clamp(state.rotation + state.velocity, -limit, limit);
      }

      updateCards(cardsRef.current);
      rafRef.current = requestAnimationFrame(tick);
    };

    readLayout();
    configRef.current.cardCount = getCardCount();
    cardsRef.current = createCards();

    if (mobileQuery.matches) {
      stateRef.current.rotation = introRef.current.from;
      introRef.current.active = true;
      introRef.current.start = performance.now();
    }

    updateCards(cardsRef.current);

    const handleResize = (): void => {
      const nextCount = getCardCount();
      if (nextCount !== configRef.current.cardCount) {
        configRef.current.cardCount = nextCount;
        arc.innerHTML = '';
        cardsRef.current = createCards();
      }
      readLayout();
      if (mobileQuery.matches && !introRef.current.active) {
        introRef.current.active = true;
        introRef.current.start = performance.now();
        stateRef.current.rotation = introRef.current.from;
      }
      const limit = getRotationLimit();
      stateRef.current.rotation = clamp(stateRef.current.rotation, -limit, limit);
      updateCards(cardsRef.current);
    };

    window.addEventListener('resize', handleResize);
    addMediaQueryListener(mobileQuery, handleResize);

    const handleMouseMove = (e: MouseEvent) => onPointerMove(e.clientX);
    const handleTouchStart = (e: TouchEvent) => {
      stateRef.current.isTouching = true;
      stateRef.current.lastX = e.touches[0].clientX;
    };
    const handleTouchMove = (e: TouchEvent) => onPointerMove(e.touches[0].clientX);
    const handleTouchEnd = () => {
      stateRef.current.isTouching = false;
      stateRef.current.lastX = null;
    };

    if (!prefersReducedMotion.matches) {
      if (!mobileQuery.matches) {
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('scroll', onScroll, { passive: true });
      }
      window.addEventListener('touchstart', handleTouchStart, { passive: true });
      window.addEventListener('touchmove', handleTouchMove, { passive: true });
      window.addEventListener('touchend', handleTouchEnd);
      rafRef.current = requestAnimationFrame(tick);
    }

    const removeMediaListener = (): void => {
      if (typeof (mobileQuery as MediaQueryList).removeEventListener === 'function') {
        (mobileQuery as MediaQueryList).removeEventListener('change', handleResize);
      } else if (
        typeof (mobileQuery as { removeListener?: (handler: () => void) => void }).removeListener === 'function'
      ) {
        (mobileQuery as { removeListener: (handler: () => void) => void }).removeListener(handleResize);
      }
    };

    return () => {
      window.removeEventListener('resize', handleResize);
      removeMediaListener();
      if (!prefersReducedMotion.matches) {
        if (!mobileQuery.matches) {
          window.removeEventListener('mousemove', handleMouseMove);
          window.removeEventListener('scroll', onScroll);
        }
        window.removeEventListener('touchstart', handleTouchStart);
        window.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('touchend', handleTouchEnd);
      }
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [arcRef, cardSrcs]);
}
